import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaHeartbeat, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import ObjectHealthApi from '../../../api/ObjectHealthApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import ObjectHealthConfigForm from './ObjectHealthConfigForm';

const ObjectHealthManager = () => {
  const api = useMemo(() => new ObjectHealthApi(), []);
  const [configs, setConfigs] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [targetFields, setTargetFields] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.fetchAll();
      if (res?.data?.success) {
        setConfigs(res.data.data || []);
      } else {
        setError(res?.data?.message || 'Failed to load configurations');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const loadObjects = async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      if (response?.success) {
        const objectList = response.data
          .filter((obj) => !obj.is_deleted)
          .map((obj) => ({
            name: obj.object_name,
            label: obj.object_label || obj.object_name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setObjects(objectList);
      }
    } catch (err) {
      console.error('Failed to load objects', err);
    }
  };

  useEffect(() => {
    loadObjects();
    loadConfigs();
  }, []);

  const openCreate = () => {
    setEditingConfig(null);
    setTargetFields([]);
    setShowForm(true);
  };

  const openEdit = async (row) => {
    setEditingConfig(row);
    setShowForm(true);
    try {
      const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(row.object_name);
      setTargetFields(res?.success && res?.data?.fields ? res.data.fields : []);
    } catch {
      setTargetFields([]);
    }
  };

  const handleSave = async (payload) => {
    const res = await api.save({
      id: payload.id,
      object_name: payload.object_name,
      is_active: payload.is_active,
      config: payload.config,
    });
    if (!res?.data?.success) {
      const msg =
        res?.data?.errors?.join?.('; ') ||
        res?.data?.message ||
        'Failed to save configuration';
      throw new Error(msg);
    }
    setSuccess('Object health configuration saved');
    setShowForm(false);
    await loadConfigs();
  };

  const handleToggle = async (row) => {
    try {
      await api.toggleActive(row.id, !row.is_active);
      await loadConfigs();
    } catch (err) {
      setError(err?.message || 'Failed to toggle configuration');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteConfig(deleteTarget.id);
      setSuccess('Configuration deleted');
      setDeleteTarget(null);
      await loadConfigs();
    } catch (err) {
      setError(err?.message || 'Failed to delete configuration');
    }
  };

  const configuredObjects = new Set(configs.map((c) => c.object_name));

  return (
    <Container fluid className="py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FaHeartbeat className="me-2 text-danger" />
            Object Health Tracking
          </h4>
          <p className="text-muted mb-0 small">
            Configure activity and stage trackers using the same criteria engine as workflows.
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openCreate}>
            <FaPlus className="me-1" />
            New config
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Trackers</th>
                  <th>Insights</th>
                  <th>Status</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No health configurations yet.
                    </td>
                  </tr>
                ) : (
                  configs.map((row) => (
                    <tr key={row.id}>
                      <td>{row.object_name}</td>
                      <td>{row.config?.trackers?.length || 0}</td>
                      <td>{row.config?.insights?.length || 0}</td>
                      <td>
                        <Badge bg={row.is_active ? 'success' : 'secondary'}>
                          {row.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-1"
                          onClick={() => openEdit(row)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          onClick={() => handleToggle(row)}
                        >
                          {row.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setDeleteTarget(row)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <ObjectHealthConfigForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSave={handleSave}
        editingConfig={editingConfig}
        objects={objects.filter((o) => !editingConfig || o.name === editingConfig.object_name || !configuredObjects.has(o.name))}
        targetFields={targetFields}
      />

      <Modal show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete health config for <strong>{deleteTarget?.object_name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ObjectHealthManager;
